import React from 'react';
import {
  Box,
  createStyles,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles, Theme, useTheme, withStyles } from "@material-ui/core/styles";

import { useRequiredEmptyAttributes, useRestaurantId } from "../../../selectors/RestaurantSelectors";
import { GuestAcquisition } from "../../../entities";
import { Channel } from "./Channel";
import channelsData from "./channels.json";

// Загружаем все картинки из текущей папки (png / jpg / jpeg / svg)
const logosContext = (require as any).context('./', false, /logo_.+\.png$/);
const logos: Record<string, string> = logosContext.keys().reduce((acc: Record<string, string>, key: string) => {
  const fileName = key.replace('./', '');
  const mod = logosContext(key);
  acc[fileName] = mod.default || mod; // поддержка ESModule/CommonJS
  return acc;
}, {});

const useStyles = makeStyles((theme: Theme) => createStyles({
  td: {
    borderBottomWidth: 0,
  },
  mobileList: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      rowGap: theme.spacing(1.5),
    },
  },
}));

const StyledHeaderCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: theme.palette.common.white,
      color: 'rgba(50, 50, 50, 0.7)',
    },
  }),
)(TableCell);


interface ChannelProps {
  channel: string;
  logo: string;
  title: string;
  url: string;
  webSiteLabel: string;
  description: string;
  details1: string;
  details2: string;
}

interface ChannelsData {
  [key: string]: ChannelProps;
}

/** @description Таблица или карточки каналов привлечения */
export function Channels() {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(960));
  const channels = channelsData as ChannelsData;
  const {
    useLatestOffer,
    useRestaurantSettings,
    isAvailableToWork,
    useRestaurantChannels,
    useSetRestaurantChannelsMutation
  } = GuestAcquisition;
  const restaurantChannelsResult = useRestaurantChannels();
  const restaurantSettingsResult = useRestaurantSettings();
  const setChannelsMutation = useSetRestaurantChannelsMutation();
  const restaurantId = useRestaurantId();
  const latestOffer = useLatestOffer();
  // @todo При изменении данных ресторана, сюда обновления о полях не доставляются
  const requiredEmptyAttributes = useRequiredEmptyAttributes();
  const isAvailable = isAvailableToWork(latestOffer, restaurantSettingsResult.data, requiredEmptyAttributes);

  if (restaurantChannelsResult.data === null || restaurantSettingsResult.data === null) {
    return null;
  }
  const availableChannels = restaurantChannelsResult.data.filter(channel => channels[channel.channel]);

  const onUpdateChannels = (channel: GM.GuestAcquisition.Channel, selected: boolean) => {
    if (setChannelsMutation.isLoading) {
      return;
    }

    const currentChannels = restaurantSettingsResult.data.channels.map((item) => ({
      channel: item.channel,
    }));

    let newChannels: Array<{ channel: GM.GuestAcquisition.Channel }> = [];

    if (selected) {
      newChannels = [
        ...currentChannels,
        { channel },
      ]
    } else {
      newChannels = currentChannels.filter((item) => item.channel !== channel);
    }

    setChannelsMutation.mutate({
      restaurantId,
      channels: newChannels,
    });
  }

  if (isMobile) {
    return (
      <Grid item xs={12}>
        <Box className={classes.mobileList}>
          {availableChannels.map((channel, index) => {
            const channelStaticInfo = channels[channel.channel];
            const settings = restaurantSettingsResult.data.channels.find((item) => item.channel === channel.channel);

            return (
              <Channel
                key={channel.channel}
                layout="card"
                title={channelStaticInfo.title}
                url={channelStaticInfo.url}
                webSiteLabel={channelStaticInfo.webSiteLabel}
                logo={logos[channelStaticInfo.logo]}
                description={channelStaticInfo.description}
                details1={channelStaticInfo.details1}
                details2={channelStaticInfo.details2}
                commissionRate={(settings && settings.billCommissionPercent) || channel.billCommissionPercent}
                selected={!!settings}
                specialConditions={!!(settings && settings.billCommissionPercentSpecial)}
                handleChange={(event, checked) => {
                  onUpdateChannels(channel.channel, checked);
                }}
                disabled={!isAvailable}
                isLast={availableChannels.length === index + 1}
              />
            );
          })}
        </Box>
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledHeaderCell width="29%">Источник</StyledHeaderCell>
              <StyledHeaderCell width="40%">Описание</StyledHeaderCell>
              <StyledHeaderCell width="29%">Ставка комиссии</StyledHeaderCell>
              <StyledHeaderCell width="1%" align="center">&bull;</StyledHeaderCell>
              <StyledHeaderCell width="1%" align="center">&bull;</StyledHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {availableChannels.map((channel, index) => {
              const channelStaticInfo = channels[channel.channel];
              const settings = restaurantSettingsResult.data.channels.find((item) => item.channel === channel.channel);

              return (
                <Channel
                  key={channel.channel}
                  title={channelStaticInfo.title}
                  url={channelStaticInfo.url}
                  webSiteLabel={channelStaticInfo.webSiteLabel}
                  logo={logos[channelStaticInfo.logo]}
                  description={channelStaticInfo.description}
                  details1={channelStaticInfo.details1}
                  details2={channelStaticInfo.details2}
                  commissionRate={(settings && settings.billCommissionPercent) || channel.billCommissionPercent}
                  selected={!!settings}
                  specialConditions={!!(settings && settings.billCommissionPercentSpecial)}
                  handleChange={(event, checked) => {
                    onUpdateChannels(channel.channel, checked);
                  }}
                  disabled={!isAvailable}
                  isLast={availableChannels.length === index + 1}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
}
